import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import {
  Alert,
  Avatar,
  alpha,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import { API_BASE } from '../apiBase';

const CompDashboard = ({ userData, onClose, navigationState }) => {
  const theme = useTheme();
  const [reports, setReports] = useState({
    userReports: [],
    officeReports: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userReportsExpanded, setUserReportsExpanded] = useState(true);
  const [officeReportsExpanded, setOfficeReportsExpanded] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    field: 'created_at',
    direction: 'desc',
  });

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`${API_BASE}/listings/dashboard`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!data.success)
        throw new Error(data.error || 'Failed to fetch reports');

      setReports({
        userReports: data.reports.filter(
          (r) => r.created_by === userData.app_user_id,
        ),
        officeReports: data.reports.filter(
          (r) => r.created_by !== userData.app_user_id,
        ),
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.app_user_id) fetchReports();
  }, [userData]);

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedReports = (reportList) => {
    return [...reportList].sort((a, b) => {
      const aValue =
        a[sortConfig.field]?.toISOString?.() || a[sortConfig.field];
      const bValue =
        b[sortConfig.field]?.toISOString?.() || b[sortConfig.field];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getCounts = (report) => {
    try {
      // Try to get counts from row_order first (more efficient)
      if (report.row_order) {
        const rowOrder =
          typeof report.row_order === 'string'
            ? JSON.parse(report.row_order)
            : report.row_order;
        return {
          offers: rowOrder.offers?.length || 0,
          feedback: rowOrder.feedback?.length || 0,
        };
      }

      // Fallback to report_data if row_order doesn't exist
      const data =
        typeof report.report_data === 'string'
          ? JSON.parse(report.report_data)
          : report.report_data;
      return {
        offers: data?.offers?.length || 0,
        feedback: data?.feedback?.length || 0,
      };
    } catch {
      return { offers: 0, feedback: 0 };
    }
  };

  const SectionHeader = ({ title, count, expanded, onToggle, icon: Icon }) => (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: alpha(theme.palette.primary.light, 0.1),
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.light, 0.15),
        },
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Icon color='primary' sx={{ mr: 1 }} />
        <Typography variant='h6'>
          {title}
          <Chip label={count} size='small' color='primary' sx={{ ml: 1 }} />
        </Typography>
      </Box>
      {expanded ? (
        <ExpandLessIcon color='primary' />
      ) : (
        <ExpandMoreIcon color='primary' />
      )}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1199,
          pointerEvents: navigationState.showDashboard ? 'auto' : 'none',
        }}
      />

      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1200,
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white',
          }}
        >
          <Typography variant='h5'>Listings Reports Dashboard</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={fetchReports} color='inherit'>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} color='inherit'>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {error && (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
              action={
                <Button color='error' size='small' onClick={fetchReports}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* My Reports */}
              <Card sx={{ mb: 3 }}>
                <SectionHeader
                  title='My Reports'
                  count={reports.userReports.length}
                  expanded={userReportsExpanded}
                  onToggle={() => setUserReportsExpanded(!userReportsExpanded)}
                  icon={AccountBoxOutlinedIcon}
                />

                <Collapse in={userReportsExpanded}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={sortConfig.field === 'title'}
                              direction={sortConfig.direction}
                              onClick={() => handleSort('title')}
                            >
                              Title
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={sortConfig.field === 'created_at'}
                              direction={sortConfig.direction}
                              onClick={() => handleSort('created_at')}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Offers</TableCell>
                          <TableCell>Feedback</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedReports(reports.userReports).map((report) => {
                          const counts = getCounts(report);
                          return (
                            <TableRow key={report.report_id}>
                              <TableCell>{report.title}</TableCell>
                              <TableCell>
                                {format(
                                  new Date(report.created_at),
                                  'MMM d, yyyy',
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip label={counts.offers} size='small' />
                              </TableCell>
                              <TableCell>
                                <Chip label={counts.feedback} size='small' />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={report.status}
                                  color={
                                    report.status === 'published'
                                      ? 'success'
                                      : report.status === 'draft'
                                        ? 'default'
                                        : 'warning'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {report.pdf_url && (
                                  <IconButton
                                    onClick={() =>
                                      window.open(report.pdf_url, '_blank')
                                    }
                                  >
                                    <OpenInNewIcon />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Card>

              {/* Office Reports */}
              <Card>
                <SectionHeader
                  title='Office Reports'
                  count={reports.officeReports.length}
                  expanded={officeReportsExpanded}
                  onToggle={() =>
                    setOfficeReportsExpanded(!officeReportsExpanded)
                  }
                  icon={ApartmentOutlinedIcon}
                />

                <Collapse in={officeReportsExpanded}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Offers</TableCell>
                          <TableCell>Feedback</TableCell>
                          <TableCell>Author</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedReports(reports.officeReports).map((report) => {
                          const counts = getCounts(report);
                          return (
                            <TableRow key={report.report_id}>
                              <TableCell>{report.title}</TableCell>
                              <TableCell>
                                {format(
                                  new Date(report.created_at),
                                  'MMM d, yyyy',
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip label={counts.offers} size='small' />
                              </TableCell>
                              <TableCell>
                                <Chip label={counts.feedback} size='small' />
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Avatar sx={{ width: 24, height: 24 }}>
                                    {report.user_id?.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography>
                                    {report.user_id?.split('@')[0]}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {report.pdf_url && (
                                  <IconButton
                                    onClick={() =>
                                      window.open(report.pdf_url, '_blank')
                                    }
                                  >
                                    <OpenInNewIcon />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Card>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

CompDashboard.propTypes = {
  userData: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  navigationState: PropTypes.shape({
    showDashboard: PropTypes.bool,
  }),
};

export default CompDashboard;
